import { createContext, useContext, useState, useEffect, useCallback } from 'react'

const LabContext = createContext()

const CONFIG = {
  PASSWORDS: {
    student: 'chem2131',
    instructor: 'instructor2131'
  }
}

const initialState = {
  isAuthenticated: false,
  authMode: null,
  studentInfo: { id: '', name: '' },
  labMode: 'guided',
  currentPart: 1,
  currentStep: 1,
  exp2Part1: {
    beakers: {
      control: { water: 0, muddy: false },
      experimental: { water: 0, muddy: false, lime: 0, alum: 0, basic: false, precipitate: 0, cleared: false }
    },
    litmusColor: 'red',
    settlingProgress: 0,
    labeled: false,
    stirred: false,
    poured: false
  },
  exp2Part2: {
    selectedTest: 'conductivity',
    testResults: {
      conductivity: {},
      chloride: {},
      flame: {},
      hardness: {}
    }
  },
  // Experiment 3: Phosphate in Cola
  exp3Part1: {
    colaVolume: 0,
    heatingProgress: 0,
    temperature: 25,
    decarbonated: false,
    cooled: false,
    diluted: false
  },
  exp3Part2: {
    stockPrepared: false,
    standards: [
      { conc: 0.00002, label: '2.0\u00d710\u207b\u2075 M', prepared: false, absorbance: null },
      { conc: 0.00005, label: '5.0\u00d710\u207b\u2075 M', prepared: false, absorbance: null },
      { conc: 0.0001, label: '1.0\u00d710\u207b\u2074 M', prepared: false, absorbance: null },
      { conc: 0.0005, label: '5.0\u00d710\u207b\u2074 M', prepared: false, absorbance: null },
      { conc: 0.00075, label: '7.5\u00d710\u207b\u2074 M', prepared: false, absorbance: null }
    ],
    reagentAdded: false,
    colorDeveloped: false,
    colorDevelopmentProgress: 0
  },
  exp3Part3: {
    wavelength: 400,
    currentSample: null,
    isZeroed: false,
    readings: {}
  },
  exp3Part4: {
    calibrationComplete: false,
    calibrationCurve: { slope: null, intercept: null, r2: null },
    unknownAbsorbance: null,
    calculatedConcentration: null
  },
  // Experiment 8: Nitrite Content Determination
  exp8Part1: {
    standards: [
      { ppm: 0, prepared: false, colorDeveloped: false, absorbance: 0 },
      { ppm: 0.1, prepared: false, colorDeveloped: false, absorbance: 0 },
      { ppm: 0.2, prepared: false, colorDeveloped: false, absorbance: 0 },
      { ppm: 0.5, prepared: false, colorDeveloped: false, absorbance: 0 },
      { ppm: 1.0, prepared: false, colorDeveloped: false, absorbance: 0 }
    ],
    unknown: { prepared: false, colorDeveloped: false, absorbance: 0 },
    reagentAdded: false,
    colorDevelopmentProgress: 0,
    timerRunning: false,
    timerValue: 0
  },
  exp8Part2: {
    wavelength: 510,
    spectrophotometerOn: true,
    isZeroed: false,
    currentReading: null,
    readings: {},
    calibrationComplete: false,
    calibrationData: { slope: null, intercept: null, r2: null },
    unknownConcentration: null
  },
  // Experiment 9: Iron Determination in Soil
  exp9Part1: {
    samplePrepared: false,
    soilMass: null,
    acidAdded: false,
    heated: false,
    heatingProgress: 0,
    filtered: false,
    diluted: false
  },
  exp9Part2: {
    hydroxylamineAdded: false,
    phenanthrolineAdded: false,
    bufferAdded: false,
    colorDeveloped: false,
    colorDevelopmentProgress: 0,
    standards: [
      { ppm: 0, prepared: false, absorbance: 0 },
      { ppm: 0.5, prepared: false, absorbance: 0 },
      { ppm: 1.0, prepared: false, absorbance: 0 },
      { ppm: 2.0, prepared: false, absorbance: 0 },
      { ppm: 4.0, prepared: false, absorbance: 0 }
    ]
  },
  exp9Part3: {
    wavelength: 510,
    spectrophotometerOn: true,
    isZeroed: false,
    currentReading: null,
    readings: {},
    calibrationComplete: false,
    calibrationData: { slope: null, intercept: null, r2: null },
    ironConcentration: null
  },
  // Experiment 1: Emission Spectra
  exp1Part1: {
    safetyAcknowledged: false,
    selectedGas: null, // 'hydrogen' | 'helium' | 'neon'
    isPowered: false
  },
  exp1Part2: {
    spectrometerAngle: 90,
    recordedLines: [], // [{angle, wavelength, color, transition}]
    currentLineIndex: 0
  },
  exp1Part3: {
    calculatedRh: null,
    percentError: null
  },
  exp1Part4: {
    heliumObserved: false,
    neonObserved: false,
    observationsRecorded: false
  },
  // Experiment 5: Soaps and Detergents
  exp5Part1: {
    oilMeasured: false,
    naohAdded: false,
    heatingProgress: 0,
    saponified: false,
    soapPrecipitated: false,
    soapMass: null
  },
  exp5Part2: {
    alcoholMeasured: false,
    acidAdded: false,
    neutralized: false,
    detergentFormed: false,
    detergentMass: null
  },
  exp5Part3: {
    foamTestComplete: false,
    hardWaterTestComplete: false,
    pHTestComplete: false,
    acidTestComplete: false,
    results: {
      soap: { foam: null, hardWater: null, pH: null, acid: null },
      detergent: { foam: null, hardWater: null, pH: null, acid: null }
    }
  },
  // Experiment 7: Gravimetric Analysis
  exp7Part1: {
    samplePrepared: false,
    sampleMass: null,
    agno3Added: false,
    precipitateFormed: false, // white AgCl
    filtered: false,
    dried: false,
    mass1: null,
    mass2: null, // constant mass check
    chloridePercent: null
  },
  exp7Part2: {
    samplePrepared: false,
    fertilizerMass: null,
    reagentAdded: false,
    precipitateFormed: false,
    filtered: false,
    dried: false,
    mass1: null,
    mass2: null,
    phosphorusPercent: null,
    p2o5Percent: null
  },
  // Experiment 10: Chemical Equilibrium
  exp10Part1: {
    initialMixed: false,
    initialColor: '#B22222', // starting equilibrium
    feAdded: false,
    scnAdded: false,
    naohAdded: false,
    currentColor: '#B22222',
    observations: []
  },
  exp10Part2: {
    pHMeterCalibrated: false,
    solutionsPrepared: false,
    pHMeasurements: [],
    calculatedKaValues: [],
    averageKa: null,
    percentError: null
  },
  // Experiment 4 - EDTA Standardization
  exp4Part1: {
    bufferAdded: false,
    indicatorAdded: false,
    solutionColor: '#8B2252' // Wine red (EBT before endpoint)
  },
  exp4Part2: {
    buretteVolume: 50,
    titrationComplete: false,
    endpointReached: false,
    volumeUsed: null
  },
  exp4Part3: {
    samplePrepared: false,
    titrationComplete: false,
    volumeUsed: null
  },
  exp4Part4: {
    hardnessCalculated: false,
    hardnessValue: null
  },
  // Experiment 6 - Vitamin C Analysis
  exp6Part1: {
    tabletsGround: false,
    sampleWeighed: false,
    sampleMass: null,
    dissolved: false
  },
  exp6Part2: {
    buretteVolume: 50,
    iodineColor: '#8B4513', // brown iodine
    flaskColor: '#FFFDE7', // pale yellow
    endpointReached: false,
    endpointTimer: 0,
    endpointValid: false, // true if persists 90s
    volumeUsed: null,
    trials: [
      { complete: false, volume: null, valid: false },
      { complete: false, volume: null, valid: false },
      { complete: false, volume: null, valid: false },
      { complete: false, volume: null, valid: false }
    ]
  },
  exp6Part3: {
    averageVolume: null,
    mgVitaminC: null,
    percentOfLabel: null
  },
  // Experiment 11 - Aspirin Determination
  exp11Part1: {
    buffer4Calibrated: false,
    buffer7Calibrated: false,
    buffer10Calibrated: false,
    meterCalibrated: false
  },
  exp11Part2: {
    sampleWeighed: false,
    sampleMass: null,
    dissolved: false
  },
  exp11Part3: {
    buretteVolume: 50,
    currentpH: 3.0,
    dataPoints: [], // [{volume, pH}]
    titrationComplete: false
  },
  exp11Part4: {
    equivalenceVolume: null,
    aspirinMass: null,
    purity: null
  },
  completedSteps: []
}

export const LabProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem('chem2131_state')
    return saved ? { ...initialState, ...JSON.parse(saved) } : initialState
  })

  useEffect(() => {
    localStorage.setItem('chem2131_state', JSON.stringify(state))
  }, [state])

  const update = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const updatePart1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp2Part1: { ...prev.exp2Part1, ...updates }
    }))
  }, [])

  const updatePart2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp2Part2: { ...prev.exp2Part2, ...updates }
    }))
  }, [])

  // Experiment 3 update functions
  const updateExp3Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp3Part1: { ...prev.exp3Part1, ...updates }
    }))
  }, [])

  const updateExp3Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp3Part2: { ...prev.exp3Part2, ...updates }
    }))
  }, [])

  const updateExp3Part3 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp3Part3: { ...prev.exp3Part3, ...updates }
    }))
  }, [])

  const updateExp3Part4 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp3Part4: { ...prev.exp3Part4, ...updates }
    }))
  }, [])

  // Experiment 8 update functions
  const updateExp8Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp8Part1: { ...prev.exp8Part1, ...updates }
    }))
  }, [])

  const updateExp8Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp8Part2: { ...prev.exp8Part2, ...updates }
    }))
  }, [])

  // Experiment 9 update functions
  const updateExp9Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp9Part1: { ...prev.exp9Part1, ...updates }
    }))
  }, [])

  const updateExp9Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp9Part2: { ...prev.exp9Part2, ...updates }
    }))
  }, [])

  const updateExp9Part3 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp9Part3: { ...prev.exp9Part3, ...updates }
    }))
  }, [])

  // Experiment 1 update functions
  const updateExp1Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp1Part1: { ...prev.exp1Part1, ...updates }
    }))
  }, [])

  const updateExp1Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp1Part2: { ...prev.exp1Part2, ...updates }
    }))
  }, [])

  const updateExp1Part3 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp1Part3: { ...prev.exp1Part3, ...updates }
    }))
  }, [])

  const updateExp1Part4 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp1Part4: { ...prev.exp1Part4, ...updates }
    }))
  }, [])

  // Experiment 5 update functions
  const updateExp5Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp5Part1: { ...prev.exp5Part1, ...updates }
    }))
  }, [])

  const updateExp5Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp5Part2: { ...prev.exp5Part2, ...updates }
    }))
  }, [])

  const updateExp5Part3 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp5Part3: { ...prev.exp5Part3, ...updates }
    }))
  }, [])

  // Experiment 7 update functions
  const updateExp7Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp7Part1: { ...prev.exp7Part1, ...updates }
    }))
  }, [])

  const updateExp7Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp7Part2: { ...prev.exp7Part2, ...updates }
    }))
  }, [])

  // Experiment 10 update functions
  const updateExp10Part1 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp10Part1: { ...prev.exp10Part1, ...updates }
    }))
  }, [])

  const updateExp10Part2 = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exp10Part2: { ...prev.exp10Part2, ...updates }
    }))
  }, [])

  const completeStep = useCallback((stepId) => {
    setState(prev => {
      if (prev.completedSteps.includes(stepId)) return prev
      const newSteps = [...prev.completedSteps, stepId]
      const stepNum = parseInt(stepId.split('-')[1]) || 0
      return {
        ...prev,
        completedSteps: newSteps,
        currentStep: prev.labMode === 'guided' ? stepNum + 1 : prev.currentStep
      }
    })
  }, [])

  const login = useCallback((mode, password, studentInfo = null) => {
    const correctPassword = mode === 'student' ? CONFIG.PASSWORDS.student : CONFIG.PASSWORDS.instructor
    if (password !== correctPassword) return false

    setState(prev => ({
      ...prev,
      isAuthenticated: true,
      authMode: mode,
      studentInfo: studentInfo || prev.studentInfo
    }))
    return true
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('chem2131_state')
    setState(initialState)
  }, [])

  const reset = useCallback(() => {
    setState(prev => ({
      ...initialState,
      isAuthenticated: prev.isAuthenticated,
      authMode: prev.authMode,
      studentInfo: prev.studentInfo
    }))
  }, [])

  const value = {
    state,
    update,
    updatePart1,
    updatePart2,
    updateExp3Part1,
    updateExp3Part2,
    updateExp3Part3,
    updateExp3Part4,
    updateExp8Part1,
    updateExp8Part2,
    updateExp9Part1,
    updateExp9Part2,
    updateExp9Part3,
    updateExp1Part1,
    updateExp1Part2,
    updateExp1Part3,
    updateExp1Part4,
    updateExp5Part1,
    updateExp5Part2,
    updateExp5Part3,
    updateExp7Part1,
    updateExp7Part2,
    updateExp10Part1,
    updateExp10Part2,
    completeStep,
    login,
    logout,
    reset,
    CONFIG
  }

  return (
    <LabContext.Provider value={value}>
      {children}
    </LabContext.Provider>
  )
}

export const useLab = () => {
  const context = useContext(LabContext)
  if (!context) {
    throw new Error('useLab must be used within a LabProvider')
  }
  return context
}

export default LabContext
